import VersusPage, {
  type VersusComparisonRow,
  type VersusFaq,
  type VersusStat,
  type VersusWorkflow,
} from "@/components/public/versus-page"
import type { SeoMeta } from "@/types"

const STATS: VersusStat[] = [
  {
    value: "60s",
    label: "listing to video",
    detail: "Paste a URL, pick a template, done. No clips to trim, no audio to sync.",
  },
  {
    value: "0",
    label: "editing skill needed",
    detail: "Negotiators and lettings admin can both ship a polished ad with zero training.",
  },
  {
    value: "50",
    label: "videos per month",
    detail: "CapCut scales with your evenings. QuiteRight scales with your listings.",
  },
  {
    value: "3",
    label: "formats at once",
    detail: "Every render comes in 9:16, 1:1 and 16:9 — no re-exporting per platform.",
  },
]

const WORKFLOW: VersusWorkflow = {
  competitorSteps: [
    "Download photos from the portal",
    "Import into CapCut, trim each to the right length",
    "Pick or cut music, align beat to cuts",
    "Type out headline and feature captions",
    "Record or buy a voiceover, align to clips",
    "Add logo, colours, contact details manually",
    "Export for Reels",
    "Re-export in 1:1 for Facebook feed",
    "Re-export in 16:9 for YouTube",
  ],
  competitorTotal: "60\u201390 minutes",
  quiterightSteps: [
    "Paste the listing URL",
    "Pick a template and voice",
    "Publish straight to Instagram, Facebook, TikTok, YouTube",
  ],
  quiterightTotal: "60 seconds",
}

const COMPARISON: VersusComparisonRow[] = [
  { label: "Built for property", competitor: "Generic video editor", quiteright: "Purpose-built for listings", highlight: true },
  { label: "Listing import", competitor: "Manual: save photos, retype details", quiteright: "Paste URL \u2014 all details auto-filled", highlight: true },
  { label: "Script writing", competitor: "Write your own", quiteright: "AI copy per property, UK tone" },
  { label: "Voiceover", competitor: "Record yourself or buy clips", quiteright: "AI voiceover, multiple UK voices", highlight: true },
  { label: "Music", competitor: "Licensed library (watermark on pro tracks)", quiteright: "Royalty-free, included" },
  { label: "Aspect ratios", competitor: "Re-export per platform", quiteright: "9:16, 1:1 and 16:9 in one render", highlight: true },
  { label: "Agency branding", competitor: "Manual each time", quiteright: "Auto on every render" },
  { label: "Publishing", competitor: "Download, upload per platform", quiteright: "Publish straight from dashboard" },
  { label: "Team consistency", competitor: "Every editor does it differently", quiteright: "Every video, same brand standard", highlight: true },
  { label: "Time per video", competitor: "60\u201390 minutes", quiteright: "Under 60 seconds", highlight: true },
]

const FAQ: VersusFaq[] = [
  {
    q: "Isn't CapCut free? Why would I pay for QuiteRight?",
    a: "CapCut is free for the editor, but the real cost is your time. An agent spending an hour per video can make maybe 5 videos a week. QuiteRight gets you there in 60 seconds \u2014 every listing gets a video, not just the ones you have time for. One extra instruction a year pays for QuiteRight several times over.",
  },
  {
    q: "Can CapCut templates work for property listings?",
    a: "CapCut has beautiful templates, but none of them know what a property is. You still have to swap every photo, rewrite every caption, time the cuts, pick music, and manually add price, bedrooms, EPC, and your branding. QuiteRight does all of that because it actually reads the listing.",
  },
  {
    q: "What about CapCut Pro for AI features?",
    a: "CapCut Pro has nice AI tools, but they're generic \u2014 AI voices that don't know estate agency copy, AI scripts that treat a property like a product video. QuiteRight's scripts are written specifically for UK property marketing, in British English, following portal tone conventions.",
  },
  {
    q: "Can the whole team use QuiteRight, or just one \"power user\"?",
    a: "Every team member gets the same templates, brand settings and output quality. With CapCut, each negotiator cuts videos differently \u2014 branding drifts, quality varies, nothing is repeatable. QuiteRight enforces your brand standard automatically for every listing.",
  },
  {
    q: "Do I have to cancel CapCut to use QuiteRight?",
    a: "No. Keep CapCut for social reels of coffee mornings, team birthdays, and client videos. Use QuiteRight for property videos specifically. Different tools for different jobs.",
  },
]

export default function VsCapcut({ meta }: { meta: SeoMeta }) {
  return (
    <VersusPage
      meta={meta}
      competitorName="CapCut"
      breadcrumbName="QuiteRight vs CapCut"
      heroHeadline="CapCut can edit anything. That's the problem"
      heroSubhead="CapCut is a fantastic general video editor. But making property videos in CapCut means starting from scratch every listing: photos, scripts, music, captions, aspect ratios. QuiteRight does all of that automatically because it's built for property."
      statsHeadline="Property videos need a property tool"
      statsSubhead="CapCut turns your agents into video editors. QuiteRight keeps them focused on winning instructions."
      stats={STATS}
      workflowHeadline="One listing, two approaches"
      workflow={WORKFLOW}
      comparison={COMPARISON}
      faq={FAQ}
      ctaHeadline="Stop editing. Start listing"
      ctaBody="Your listings deserve purpose-built video marketing, not a general-purpose editor."
      relatedKey="vs/capcut"
    />
  )
}
